import * as z from 'zod';
import type { TimeData } from '../../core/TimeData';
import {
  type CoaEvent,
  type EventOrLeg,
  type Events,
  isCargoEvent,
  type Leg,
  type OffHireEvent,
  type SpotEvent,
  type TCOrderEvent,
  type WaitEvent,
} from '../../core/TimeData';
import type { ClientId } from '@seaber/common/lib/clientId';
import type { ScenarioOrder } from '../../../lib/planningData';
import { DefaultMap } from '@seaber/common/lib/collections';
import { EventCategory } from '@seaber/common/core/entries';
import { formatDays } from '../../../lib/util';
import { dToH } from '../../../lib/time';
import { brandedZodNumber } from '../../../lib/zod-util';
import type { OrderId } from '@seaber/common/graphql/types';

// TODO rename groupOrdersByPortCallId or maybe remove completely if getRelatedOrder is no longer used https://github.com/Seaber-io/frontend/pull/626#discussion_r705071343
export const groupOrdersByPortCallId = (
  timedata: TimeData[],
): Map<ClientId, ScenarioOrder[]> => {
  const ordersByPortCallId = new DefaultMap<ClientId, ScenarioOrder[]>(
    () => [],
  );
  for (const { event: e } of timedata) {
    if (isCargoEvent(e) && e.portCallId !== null) {
      ordersByPortCallId.get(e.portCallId).push(e.order);
    }
  }
  return ordersByPortCallId;
};

// TODO: getRelatedOrder in ScenarioTimeline seems odd given current data structures. could perhaps be dropped or at least written in a more obvious way to whatever its purpose was https://github.com/Seaber-io/frontend/pull/626#discussion_r703433733
export const getRelatedOrder = (
  ordersByPortCallId: Map<ClientId, ScenarioOrder[]>,
  e: Events,
): ScenarioOrder | null =>
  'order' in e
    ? e.order
    : ('portCallId' in e &&
        e.portCallId != null &&
        ordersByPortCallId.get(e.portCallId)?.[0]) ||
      null;
export const freeEventFilter = (
  e: TimeData,
): e is TimeData<
  Exclude<Events, TCOrderEvent | SpotEvent | CoaEvent | OffHireEvent>
> => {
  const { category } = e.event;
  return (
    category !== EventCategory.TC &&
    category !== EventCategory.Spot &&
    category !== EventCategory.COA &&
    category !== EventCategory.OffHire &&
    category !== EventCategory.Static
  );
};
export const isLegOrWait = (e: EventOrLeg): e is Leg | WaitEvent =>
  'category' in e;

export const dropOrdersToTimelineSchema = z.object({
  type: z.enum(['orders']),
  selectedOrderIds: z.array(brandedZodNumber<OrderId>()),
  dragOrderId: brandedZodNumber<OrderId>().nullable(),
});
const dnDShipmentsOnTimelineSchema = z.object({
  type: z.enum(['shipments']),
  orderIds: z.array(brandedZodNumber<OrderId>()),
  singleEventIds: z.array(z.string()),
  isWholeShipment: z.boolean(),
});
const dnDSingleEventOnTimelineSchema = z.object({
  type: z.enum(['single_event']),
  singleEventId: z.string(),
});
export const dragDropDataSchema = z.union([
  dropOrdersToTimelineSchema,
  dnDShipmentsOnTimelineSchema,
  dnDSingleEventOnTimelineSchema,
]);

export function snapTimeTo1HGrid(time: number | Date): Date {
  const date = new Date(time);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    0,
    0,
    // This is a small workaround to give space for ETA and ETB timestamps.
    // They are 2 and 4 ms behind loadingStart timestamp
    // Without this ETA and ETB will have time with minutes=59
    50,
  );
}

/**
 * Given a number of days, returns a formatted total voyage duration in days and hours
 * e.g "3.4 days (81 h)"
 * If < 1 day, returns only hours, e.g. "23 h"
 * @param totalDays number of days
 */
export function formatTotalVoyageDuration(totalDays: number): string {
  if (totalDays < 1) {
    return `${Math.round(dToH(totalDays))} h`;
  } else {
    return `${formatDays(totalDays)} (${Math.round(dToH(totalDays))} h)`;
  }
}
